// media-paths.js — R2 media URL resolution (Brief 003).
//
// The demo's media lives on Cloudflare R2 behind cdn.speechgenie.online, laid
// out as `l2b/images/<stem>.png` and `l2b/audio/<stem>.ogg` (Brief 002). Unlike
// KFE Web — which HEAD-probes .m4a-then-.ogg candidates against a local
// StreamingAssets mirror — the demo resolves by **stem** against that single R2
// base. R2 keys are case-sensitive, so the stem's original casing is preserved
// verbatim (the content provider carries `imageStem` / `audioStem` straight from
// `demo-mmus.json`); never lower-case or re-derive from a literal filename.
//
// Gotcha (Brief 003 §5): the Unity source names audio `.m4a`, but the real R2
// files are `.ogg`. Resolve by stem + the `.ogg` extension here — do NOT trust
// any `audioFileName`.
//
// Brief 004 (language onboarding): the L2B screens now route audio through
// `app/i18n/language-manager.js`, which owns the two-axis derivation —
// `targetAudioUrl` (L2/English, by audioStem) and `l1AudioUrl` (L1/Mandarin,
// derived from the phrase). `resolveAudio` below stays as the audio-engine's
// default for a bare MMU/URL (it resolves the English-target `.ogg`).

/** R2 media base. Trailing slash included so callers concatenate cleanly. */
export const mediaBase = "https://cdn.speechgenie.online/l2b/";

/**
 * Best-supported audio file extension for the current browser (Brief 006a).
 *
 * The audio set ships in two formats on R2: the original Ogg Vorbis (`.ogg`) and
 * AAC (`.m4a`). WebKit — Safari and every iOS browser — CANNOT decode Ogg Vorbis
 * (`canPlayType('audio/ogg')` → `""`), so the live build was silent on Apple
 * devices even though the file 200s and the tap reaches `play()`. AAC is
 * universally supported (Chrome / Firefox / Safari / iOS), so we prefer `.m4a`
 * wherever the engine reports it can play it, and fall back to `.ogg` only for an
 * (essentially hypothetical) AAC-less engine. Probed once and cached; a non-DOM
 * context defaults to the broad-support `.m4a`.
 * @returns {"m4a" | "ogg"}
 */
let _audioExt;
export function audioExt() {
  if (_audioExt) return _audioExt;
  try {
    const el = document.createElement("audio");
    _audioExt = el.canPlayType('audio/mp4; codecs="mp4a.40.2"') ? "m4a" : "ogg";
  } catch {
    _audioExt = "m4a";
  }
  return _audioExt;
}

/**
 * Lowercased, whitespace-stripped, extension-shorn stem of a phrase or filename.
 * Kept for parity with KFE's `stem()` (used for `wordKey` derivation in the
 * content provider). NB: this lower-cases — only use it where casing is
 * irrelevant. Media stems come PRE-CASED from `demo-mmus.json`; resolve those
 * via the `imageStem`/`audioStem` fields, never by re-stemming a phrase here.
 * @param {string} s
 * @returns {string}
 */
export function stem(s) {
  return String(s).trim().toLowerCase().replace(/\s+/g, "").replace(/\.[^.]+$/, "");
}

/**
 * Pull a usable media stem out of an MMU-shaped target or a bare stem string.
 * @param {string | { imageStem?: string, audioStem?: string } | null | undefined} target
 * @param {"imageStem" | "audioStem"} field
 * @returns {string}
 */
function stemOf(target, field) {
  if (typeof target === "string") return target;
  return (target && target[field]) || "";
}

/**
 * Resolve an L2B image URL. Images are transparent-PNG line drawings under
 * `l2b/images/<stem>.png`. Accepts an MMU (reads `imageStem`) or a bare stem.
 * Async to match KFE's `resolveImage` signature so the copied drag-area /
 * exercise screens `await` it unchanged.
 * @param {string | { imageStem?: string } | null | undefined} target
 * @returns {Promise<string | undefined>}
 */
export async function resolveImage(target) {
  const s = stemOf(target, "imageStem");
  if (!s) return undefined;
  return `${mediaBase}images/${s}.png`;
}

/**
 * Resolve a target-audio URL. Audio is `l2b/audio/<stem>.<ext>`, where the ext
 * is the browser's best-supported format (`audioExt()` — `.m4a`, with `.ogg` as
 * a fallback; Brief 006a). Accepts an MMU (reads `audioStem`) or a bare stem; an
 * explicit `url` short-circuits (the verbatim `core/audio-engine.js` passes
 * through here). Async to match KFE.
 * @param {string | { url?: string, audioStem?: string } | null | undefined} target
 * @returns {Promise<string | undefined>}
 */
export async function resolveAudio(target) {
  if (target && typeof target === "object" && target.url) return target.url;
  const s = stemOf(target, "audioStem");
  if (!s) return undefined;
  return `${mediaBase}audio/${s}.${audioExt()}`;
}
